import { useState, useEffect, useCallback } from 'react'
import { X, Check, Crown, Loader2, AlertCircle, ExternalLink, Zap, Star } from 'lucide-react'
import api from '../lib/api'
import { useSubscription } from '../contexts/SubscriptionContext'
import { useLocale } from '../contexts/LocaleContext'

interface UpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  feature?: string
  defaultPlan?: 'pro' | 'business'
}

export default function UpgradeModal({ isOpen, onClose, feature, defaultPlan = 'pro' }: UpgradeModalProps) {
  const { tx } = useLocale()
  const { subscription, usage } = useSubscription()
  const currentPlan = subscription?.plan || 'free'
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [paypalConfigured, setPaypalConfigured] = useState(false)
  const [checkingConfig, setCheckingConfig] = useState(true)
  const [selectedPlan, setSelectedPlan] = useState<'pro' | 'business'>(defaultPlan)

  useEffect(() => {
    if (isOpen) {
      // Auto-select the next available plan (skip current)
      const autoPlan = currentPlan === 'pro' ? 'business' : currentPlan === 'business' ? 'pro' : defaultPlan
      setSelectedPlan(autoPlan)
      api.getPayPalConfig().then(config => {
        setPaypalConfigured(config.configured)
        setCheckingConfig(false)
      }).catch(() => {
        setPaypalConfigured(false)
        setCheckingConfig(false)
      })
    }
  }, [isOpen, defaultPlan])

  const handleUpgrade = useCallback(async () => {
    setLoading(true)
    setError(null)

    try {
      const baseUrl = window.location.origin
      const result = await api.createSubscription(
        selectedPlan,
        `${baseUrl}/?subscription_activated=1`,
        `${baseUrl}/?subscription_cancelled=1`
      )

      if (result.approval_url) {
        window.location.href = result.approval_url
      } else {
        setError(tx('No payment link received. Please try again.', 'לא התקבל קישור לתשלום. נסה שוב.'))
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : tx('Error creating subscription', 'שגיאה ביצירת מנוי'))
    } finally {
      setLoading(false)
    }
  }, [selectedPlan])

  if (!isOpen) return null

  const atLimit = usage.proposals_remaining === 0
  const proposalsSent = usage.proposals_count
  const proposalsLimit = usage.proposals_limit

  const plans = [
    {
      id: 'pro' as const,
      name: tx('Pro', 'פרו'),
      price: 12,
      icon: <Crown className="w-6 h-6" />,
      color: 'indigo',
      features: [
        tx('Send up to 20 proposals/month — close more deals', 'שלח עד 20 הצעות לחודש — סגור יותר עסקאות'),
        tx('Create proposals in seconds with AI', 'צור הצעות תוך שניות עם AI'),
        tx('Export to PDF and look professional', 'ייצוא ל-PDF ומראה מקצועי'),
        tx('Start faster with ready-made templates', 'התחל מהר עם תבניות מוכנות'),
        tx('Know exactly when clients open your proposals', 'דע מתי לקוחות פותחים את ההצעות שלך'),
        tx('Manage all your clients in one place', 'נהל את כל הלקוחות שלך במקום אחד'),
        tx('Get notified the moment a proposal is signed', 'קבל התראה ברגע שחותמים'),
      ],
    },
    {
      id: 'business' as const,
      name: tx('Business', 'עסקי'),
      price: 39,
      icon: <Zap className="w-6 h-6" />,
      color: 'purple',
      features: [
        tx('Send unlimited proposals — never get blocked', 'שלח הצעות ללא הגבלה — לעולם אל תיחסם'),
        tx('Create proposals in seconds with AI', 'צור הצעות תוך שניות עם AI'),
        tx('Export to PDF and look professional', 'ייצוא ל-PDF ומראה מקצועי'),
        tx('Start faster with ready-made templates', 'התחל מהר עם תבניות מוכנות'),
        tx('Know exactly when clients open your proposals', 'דע מתי לקוחות פותחים את ההצעות שלך'),
        tx('Manage all your clients in one place', 'נהל את כל הלקוחות שלך במקום אחד'),
        tx('Get notified the moment a proposal is signed', 'קבל התראה ברגע שחותמים'),
        tx('Skip the queue with priority support', 'דלג בתור עם תמיכה עדיפה'),
      ],
    },
  ]

  // Derive headline based on context
  const headline = currentPlan === 'business'
    ? tx('Change Plan', 'שנה תוכנית')
    : atLimit
      ? tx("You've hit your free limit — don't lose this deal", 'הגעת למגבלה החינמית — אל תפספס את העסקה')
      : tx('Upgrade to keep closing deals', 'שדרג וסגור יותר עסקאות')

  const subheadline = atLimit && currentPlan === 'free'
    ? tx(
        `You've sent ${proposalsSent} of ${proposalsLimit} free proposals. Upgrade now to keep sending.`,
        `שלחת ${proposalsSent} מתוך ${proposalsLimit} הצעות חינמיות. שדרג עכשיו כדי להמשיך.`
      )
    : feature
      ? `${feature} ${tx('is available on paid plans', 'זמין בתוכניות בתשלום')}`
      : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-primary-700 to-accent-500 p-6 text-white relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            aria-label={tx('Close', 'סגור')}
          >
            <X className="w-5 h-5" />
          </button>
          <h2 className="text-2xl font-bold pr-8">{headline}</h2>
          {subheadline && (
            <p className="text-white/85 text-sm mt-1.5">{subheadline}</p>
          )}
        </div>

        {/* Social proof bar */}
        {currentPlan !== 'business' && (
          <div className="flex items-center justify-center gap-2 bg-violet-50 border-b border-violet-100 py-2.5 px-4">
            <div className="flex -space-x-1">
              {['bg-pink-400', 'bg-violet-400', 'bg-indigo-400'].map((c, i) => (
                <div key={i} className={`w-5 h-5 rounded-full ${c} border-2 border-white`} />
              ))}
            </div>
            <span className="text-xs text-violet-700 font-medium">
              {tx('1,200+ businesses upgraded to Pro', 'יותר מ-1,200 עסקים כבר שדרגו לפרו')}
            </span>
            <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
          </div>
        )}

        {/* Plan Cards */}
        <div className="p-6">
          <div className="grid grid-cols-2 gap-4 mb-5">
            {plans.map((plan) => {
              const isSelected = selectedPlan === plan.id
              const isCurrent = currentPlan === plan.id
              return (
                <button
                  key={plan.id}
                  onClick={() => !isCurrent && setSelectedPlan(plan.id)}
                  disabled={isCurrent}
                  className={`text-left p-5 rounded-xl border-2 transition-all ${
                    isCurrent
                      ? 'border-slate-300 bg-slate-50 opacity-60 cursor-not-allowed'
                      : isSelected
                        ? 'border-violet-500 bg-violet-50/40 shadow-md'
                        : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      plan.id === 'pro'
                        ? 'bg-violet-100 text-violet-700'
                        : 'bg-rose-100 text-rose-600'
                    }`}>
                      {plan.icon}
                    </div>
                    <span className="font-bold text-slate-900">{plan.name}</span>
                    {isCurrent ? (
                      <span className="text-[10px] bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-full font-medium">
                        {tx('CURRENT', 'נוכחי')}
                      </span>
                    ) : plan.id === 'business' ? (
                      <span className="text-[10px] bg-rose-100 text-rose-700 px-1.5 py-0.5 rounded-full font-medium">
                        {tx('POPULAR', 'פופולרי')}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-slate-900">${plan.price}</span>
                    <span className="text-slate-500 text-sm">/{tx('month', 'חודש')}</span>
                  </div>
                  <ul className="space-y-2">
                    {plan.features.map((feat, i) => (
                      <li key={i} className="flex items-start gap-2 text-xs text-slate-600">
                        <Check className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0 mt-0.5" />
                        {feat}
                      </li>
                    ))}
                  </ul>
                </button>
              )
            })}
          </div>

          {/* Testimonial */}
          {currentPlan !== 'business' && (
            <blockquote className="flex items-start gap-3 bg-slate-50 rounded-xl px-4 py-3 mb-5 border border-slate-100">
              <Star className="w-4 h-4 text-amber-400 fill-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600 italic">
                {tx(
                  '"Closed a $4,000 project the same day I sent my first Pro proposal. Worth every dollar."',
                  '"סגרתי פרויקט של 14,000₪ ביום שבו שלחתי את ההצעה הראשונה שלי בפרו. שווה כל שקל."'
                )}
                <span className="not-italic font-medium text-slate-500 block mt-1">— {tx('Daniel R., Freelance Designer', 'דניאל ר., מעצב עצמאי')}</span>
              </p>
            </blockquote>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl mb-4 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* CTA */}
          {checkingConfig ? (
            <div className="flex items-center justify-center py-3 text-slate-400">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
          ) : paypalConfigured ? (
            <button
              onClick={handleUpgrade}
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-primary-700 to-accent-500 text-white py-3.5 px-4 rounded-xl font-semibold hover:opacity-90 transition-all disabled:opacity-50 shadow-lg shadow-accent-500/25 text-base"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <span>
                    {selectedPlan === 'pro' && currentPlan === 'business'
                      ? tx('Switch to Pro — $12/month', 'עבור לפרו — $12/חודש')
                      : selectedPlan === 'pro'
                        ? tx('Start Pro — $12/month', 'התחל פרו — $12/חודש')
                        : tx('Start Business — $39/month', 'התחל עסקי — $39/חודש')
                    }
                  </span>
                  <ExternalLink className="w-4 h-4" />
                </>
              )}
            </button>
          ) : (
            <div className="text-center text-sm text-slate-500 bg-slate-50 rounded-xl p-4">
              {tx('Payment system is being configured. Contact us for manual upgrade.', 'מערכת התשלום בהגדרה. צור קשר לשדרוג ידני.')}
            </div>
          )}

          {/* Risk reversal */}
          <p className="text-center text-xs text-slate-400 mt-3">
            {tx('Cancel anytime. No contracts. No hidden fees.', 'בטל בכל עת. ללא חוזים. ללא עמלות נסתרות.')}
          </p>

          {/* Maybe later — secondary, low-friction */}
          <div className="text-center mt-3">
            <button
              onClick={onClose}
              className="text-xs text-slate-400 hover:text-slate-600 underline underline-offset-2 transition-colors"
            >
              {tx('Maybe later', 'אולי אחר כך')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
